import React from 'react';

interface GolfLoadingSpinnerProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
}

const GolfLoadingSpinner: React.FC<GolfLoadingSpinnerProps> = ({
  message = 'Loading...',
  size = 'medium'
}) => {
  const sizeClasses = {
    small: 'w-32 h-32',
    medium: 'w-48 h-48',
    large: 'w-64 h-64'
  };

  const ballSizes = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-6 h-6'
  };

  const holeSizes = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  };

  return (
    <div className="flex flex-col items-center justify-center p-8">
      {/* Main Container */}
      <div className={`relative ${sizeClasses[size]} flex items-center justify-center overflow-hidden bg-green/10 rounded-full border-4 border-green-ink shadow-xl`}>
        
        {/* Grass Background */}
        <div className="absolute inset-0 bg-green-ink opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green via-green-ink to-green-ink"></div>

        <div className="relative w-full h-full flex items-center justify-center">
          
          {/* THE HOLE & FLAG */}
          <div className={`relative ${holeSizes[size]} bg-gray-900 rounded-full shadow-inner z-10 flex items-center justify-center`}>
            {/* Inner lip shadow */}
            <div className="absolute inset-0 rounded-full shadow-[inset_0_2px_4px_rgba(0,0,0,1)]"></div>
            
            {/* Flag Stick - Centered */}
            <div className="absolute bottom-1/2 left-1/2 w-0.5 h-16 bg-gray-100 origin-bottom -translate-x-1/2 z-0">
               {/* Flag */}
               <div className="absolute top-0 right-0 w-6 h-4 bg-red-500 rounded-sm origin-left animate-[wave_2s_ease-in-out_infinite]"></div>
            </div>
          </div>

          {/* ANIMATION WRAPPER */}
          {/* We use a wrapper to handle the complex trajectory keyframes */}
          <div className="absolute z-30" style={{ animation: 'bounce-roll-drop 2s linear infinite' }}>
            
            {/* BALL (Handles Rotation) */}
            <div className={`${ballSizes[size]} bg-white rounded-full shadow-sm overflow-hidden relative`}
                 style={{ animation: 'spin 2s linear infinite' }}>
                {/* Dimples */}
                <div className="absolute top-1 left-1 w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
                <div className="absolute bottom-2 right-1.5 w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
                <div className="absolute top-2 right-1 w-0.5 h-0.5 bg-gray-300 rounded-full"></div>
            </div>

            {/* SHADOW (Handles pulsing when ball bounces) */}
            <div className="absolute -bottom-2 left-0 w-full h-1.5 bg-black rounded-full blur-[2px]"
                 style={{ animation: 'shadow-pulse 2s linear infinite' }}>
            </div>
            
          </div>
        </div>
      </div>

      {/* Message */}
      <div className="mt-6 text-center">
        <p className="text-sm font-bold tracking-widest text-green-ink uppercase animate-pulse">{message}</p>
      </div>

      <style>{`
        @keyframes wave {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-8deg); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          80% { transform: rotate(360deg); } /* Stop spinning when it drops */
          100% { transform: rotate(360deg); }
        }

        /* The Physics Engine 
           X axis: Moves constantly right
           Y axis: Bounces (Negative is UP)
        */
        @keyframes bounce-roll-drop {
          0% {
            /* Start: Far left, In the air */
            transform: translate(-80px, -40px);
            opacity: 0;
          }
          10% { opacity: 1; }

          /* First Bounce (Large) */
          20% { transform: translate(-60px, 0px); animation-timing-function: ease-out; }
          30% { transform: translate(-50px, -20px); animation-timing-function: ease-in; }
          
          /* Second Bounce (Medium) */
          40% { transform: translate(-40px, 0px); animation-timing-function: ease-out; }
          50% { transform: translate(-32px, -8px); animation-timing-function: ease-in; }

          /* Third Bounce (Small Hop) */
          60% { transform: translate(-24px, 0px); animation-timing-function: ease-out; }
          65% { transform: translate(-20px, -3px); animation-timing-function: ease-in; }
          
          /* Land and Flat Roll */
          70% { transform: translate(-16px, 0px); } 
          85% { transform: translate(0px, 0px) scale(1); opacity: 1; } /* Reaches hole */

          /* The Drop */
          95% { transform: translate(0px, 5px) scale(0.4); opacity: 0; }
          100% { transform: translate(0px, 5px) scale(0); opacity: 0; }
        }

        /* Shadow gets light when ball is high, dark when low */
        @keyframes shadow-pulse {
          0% { opacity: 0; transform: scale(0.5); }
          20% { opacity: 0.6; transform: scale(1); } /* Land 1 */
          30% { opacity: 0.2; transform: scale(0.6); } /* Peak 1 */
          40% { opacity: 0.6; transform: scale(1); } /* Land 2 */
          50% { opacity: 0.3; transform: scale(0.7); } /* Peak 2 */
          60% { opacity: 0.6; transform: scale(1); } /* Land 3 */
          65% { opacity: 0.4; transform: scale(0.8); } /* Peak 3 */
          70% { opacity: 0.6; transform: scale(1); } /* Rolling starts */
          90% { opacity: 0; transform: scale(0); } /* Disappears in hole */
        }
      `}</style>
    </div>
  );
};

export default GolfLoadingSpinner;